/**
 * cues-structure.ts — procedural structure SFX builders (v2M8.6).
 *
 * Implements AUDIO §8's three single-shot structure cues. The two
 * looping ambient cues (Extractor churn, Repair Yard shimmer) plus
 * the construction-loop pulse defer to v3M11 polish — looped + per-
 * structure-instance state is meaningfully more infrastructure than
 * the diff path.
 *
 *   - **structure:construction-start** (§8) — short power-up hum,
 *     600 ms, positional. Fires when a planet-side structure first
 *     appears in `state.structures` (build queued).
 *   - **structure:complete** (§8) — warm chime, 800 ms, non-positional.
 *     Fires when a structure's `buildTicksRemaining` transitions
 *     from > 0 to 0 (initial build OR upgrade). Layers cleanly with
 *     `alert:green` if the player owns the planet.
 *   - **structure:destroyed** (§8) — heavy rumble + collapsing synth
 *     tail, 1.2 s, positional. Fires when an entry vanishes from
 *     `state.structures`.
 */

import type { AudioEngine } from './engine';
import type { CueVariation } from './registry';

const NEUTRAL: CueVariation = { pitchMul: 1, gainMul: 1 };

export function registerStructureCues(engine: AudioEngine): void {
  engine.registry.registerProcedural(
    'structure:construction-start',
    'sfx',
    buildConstructionStart,
  );
  engine.registry.registerProcedural('structure:complete', 'sfx', buildStructureComplete);
  engine.registry.registerProcedural('structure:destroyed', 'sfx', buildStructureDestroyed);
}

/**
 * Power-up hum — rising sawtooth fundamental + 5th harmonic with a
 * lowpass filter sweeping open over 600 ms. Reads as "engineering
 * spinning up", contained but assertive.
 */
function buildConstructionStart(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  const fundamental = ctx.createOscillator();
  fundamental.type = 'sawtooth';
  fundamental.frequency.setValueAtTime(110 * variation.pitchMul, now);
  fundamental.frequency.exponentialRampToValueAtTime(165 * variation.pitchMul, now + 0.55);
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 1.5;
  filter.frequency.setValueAtTime(220, now);
  filter.frequency.exponentialRampToValueAtTime(1100, now + 0.45);
  const fundGain = ctx.createGain();
  fundGain.gain.setValueAtTime(0.0001, now);
  fundGain.gain.exponentialRampToValueAtTime(0.22 * variation.gainMul, now + 0.1);
  fundGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
  fundamental.connect(filter);
  filter.connect(fundGain);
  fundGain.connect(target);
  fundamental.start(now);
  fundamental.stop(now + 0.61);
  // 5th harmonic — softer, adds bite without dominating.
  const harmonic = ctx.createOscillator();
  harmonic.type = 'triangle';
  harmonic.frequency.setValueAtTime(330 * variation.pitchMul, now);
  harmonic.frequency.exponentialRampToValueAtTime(495 * variation.pitchMul, now + 0.55);
  const harmGain = ctx.createGain();
  harmGain.gain.setValueAtTime(0.0001, now);
  harmGain.gain.exponentialRampToValueAtTime(0.1 * variation.gainMul, now + 0.15);
  harmGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
  harmonic.connect(harmGain);
  harmGain.connect(target);
  harmonic.start(now);
  harmonic.stop(now + 0.61);
  return fundamental;
}

/**
 * Warm chime — major-third dyad on sine + slight detune for shimmer,
 * 800 ms decay. Distinct from the alert:green stinger (which is a
 * shorter rising bell) so the two layer pleasantly when the player
 * completes a structure on their own planet.
 */
function buildStructureComplete(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Root tone — C5 (523 Hz).
  const root = ctx.createOscillator();
  root.type = 'sine';
  root.frequency.value = 523 * variation.pitchMul;
  const rootGain = ctx.createGain();
  rootGain.gain.setValueAtTime(0.0001, now);
  rootGain.gain.exponentialRampToValueAtTime(0.28 * variation.gainMul, now + 0.04);
  rootGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.8);
  root.connect(rootGain);
  rootGain.connect(target);
  root.start(now);
  root.stop(now + 0.81);
  // Major third — E5 (659 Hz). Slightly delayed for chord-like layering.
  const third = ctx.createOscillator();
  third.type = 'sine';
  third.frequency.value = 659 * variation.pitchMul;
  const thirdGain = ctx.createGain();
  thirdGain.gain.setValueAtTime(0.0001, now + 0.06);
  thirdGain.gain.exponentialRampToValueAtTime(0.22 * variation.gainMul, now + 0.1);
  thirdGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.8);
  third.connect(thirdGain);
  thirdGain.connect(target);
  third.start(now + 0.06);
  third.stop(now + 0.81);
  // Slight detune for shimmer (root + 4 cents).
  const shimmer = ctx.createOscillator();
  shimmer.type = 'sine';
  shimmer.frequency.value = 524.2 * variation.pitchMul;
  const shimmerGain = ctx.createGain();
  shimmerGain.gain.setValueAtTime(0.0001, now);
  shimmerGain.gain.exponentialRampToValueAtTime(0.08 * variation.gainMul, now + 0.04);
  shimmerGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.8);
  shimmer.connect(shimmerGain);
  shimmerGain.connect(target);
  shimmer.start(now);
  shimmer.stop(now + 0.81);
  return root;
}

/**
 * Heavy rumble + collapsing synth tail, 1.2 s. Two-stage shape:
 * sub-bass impact at the start, then a downward-sweeping low-pass
 * filter on broadband noise simulating structural collapse.
 */
function buildStructureDestroyed(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Sub-bass impact — sine sweep 100 → 35 Hz over 500 ms, hard attack.
  const impact = ctx.createOscillator();
  impact.type = 'sine';
  impact.frequency.setValueAtTime(100 * variation.pitchMul, now);
  impact.frequency.exponentialRampToValueAtTime(35 * variation.pitchMul, now + 0.5);
  const impactGain = ctx.createGain();
  impactGain.gain.setValueAtTime(0.0001, now);
  impactGain.gain.exponentialRampToValueAtTime(0.5 * variation.gainMul, now + 0.005);
  impactGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
  impact.connect(impactGain);
  impactGain.connect(target);
  impact.start(now);
  impact.stop(now + 0.61);
  // Collapse tail — broadband noise with downward filter sweep.
  const tail = createNoiseSource(ctx, 1.2);
  const tailFilter = ctx.createBiquadFilter();
  tailFilter.type = 'lowpass';
  tailFilter.Q.value = 1;
  tailFilter.frequency.setValueAtTime(1800, now);
  tailFilter.frequency.exponentialRampToValueAtTime(120, now + 1.1);
  const tailGain = ctx.createGain();
  tailGain.gain.setValueAtTime(0.0001, now);
  tailGain.gain.exponentialRampToValueAtTime(0.32 * variation.gainMul, now + 0.04);
  tailGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
  tail.connect(tailFilter);
  tailFilter.connect(tailGain);
  tailGain.connect(target);
  tail.start(now);
  tail.stop(now + 1.21);
  // Low synth groan — sawtooth with vibrato, joins partway through.
  const groan = ctx.createOscillator();
  groan.type = 'sawtooth';
  groan.frequency.setValueAtTime(70 * variation.pitchMul, now);
  groan.frequency.exponentialRampToValueAtTime(45 * variation.pitchMul, now + 1.0);
  const groanGain = ctx.createGain();
  groanGain.gain.setValueAtTime(0.0001, now + 0.1);
  groanGain.gain.exponentialRampToValueAtTime(0.22 * variation.gainMul, now + 0.2);
  groanGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
  groan.connect(groanGain);
  groanGain.connect(target);
  groan.start(now + 0.1);
  groan.stop(now + 1.21);
  return impact;
}

function createNoiseSource(ctx: AudioContext, durationSec: number): AudioBufferSourceNode {
  const length = Math.max(1, Math.floor(ctx.sampleRate * durationSec));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  return source;
}
